import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { SelectTextField } from '@lumapps/combobox/components/SelectTextField';
import { homePageView } from '@lumapps/contents/routes';
import { InstancePickerField } from '@lumapps/instance-ui/components/InstancePickerField';
import { getCurrentInstance } from '@lumapps/instance/ducks/selectors';
import { useInstanceFavIcon, defaultFavIcon } from '@lumapps/instance/hooks/useInstanceFavIcon';
import type { Instance } from '@lumapps/instance/types';
import { FlexBox, Thumbnail, Tooltip, Text, Placement, REAL_SIZE_FOR_LUMX_SIZE } from '@lumapps/lumx/react';
import { isInstanceSuperAdmin, isSuperAdmin, instancesSuperAdmin } from '@lumapps/permissions';
import { useSelector } from '@lumapps/redux/react';
import { Link as RouterLink, useRouter } from '@lumapps/router';
import { useCurrentRoute } from '@lumapps/router/hooks/useCurrentRoute';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import './index.scss';
import { useInstanceSwitcherRedirect } from '../../hooks/useInstanceSwitcherRedirect';

export interface InstanceSwitcherProps {}

export const CLASSNAME = 'site-switcher';

/**
 * Component to be used within the top bar in the back office for switching sites.
 * @param InstanceSwitcherProps
 * @returns InstanceSwitcher
 */
export const InstanceSwitcher: React.FC<InstanceSwitcherProps> = () => {
    const isGlobalAdmin = useSelector(isSuperAdmin);
    const isInstanceAdmin = useSelector(isInstanceSuperAdmin);
    const instancesWhereUserIsAdmin = useSelector(instancesSuperAdmin);
    const { redirect } = useRouter();
    const currentRoute = useCurrentRoute();
    const { element } = useClassnames(CLASSNAME);
    const { translateAndReplace } = useTranslate();
    const currentInstance = useSelector(getCurrentInstance);
    const getInstanceFavIcon = useInstanceFavIcon();
    const currentInstanceImage = getInstanceFavIcon(currentInstance);
    const canSeeInstanceSwitcher = isGlobalAdmin || (isInstanceAdmin && instancesWhereUserIsAdmin.length > 0);

    const { set } = useInstanceSwitcherRedirect();

    const onChange = (instance: Instance) => {
        const { slug } = instance;

        set();

        redirect({
            ...currentRoute,
            instanceSlug: slug,
        });
    };

    return (
        <FlexBox orientation="horizontal" fillSpace hAlign="center">
            <Tooltip
                label={translateAndReplace(GLOBAL.HOME_PAGE, { site: currentInstance?.name })}
                placement={Placement.RIGHT}
            >
                <RouterLink className={element('logo-link')} to={homePageView({})} target="_self">
                    <Thumbnail
                        alt=""
                        className={element('logo')}
                        image={currentInstanceImage}
                        aspectRatio="free"
                        size="l"
                        fallback={<Thumbnail alt="" image={defaultFavIcon} size="s" />}
                        imgProps={{
                            width: REAL_SIZE_FOR_LUMX_SIZE.l,
                            height: REAL_SIZE_FOR_LUMX_SIZE.l,
                        }}
                    />
                </RouterLink>
            </Tooltip>
            {canSeeInstanceSwitcher ? (
                <InstancePickerField
                    value={currentInstance}
                    onChange={onChange}
                    selectionType="single"
                    selectTextFieldProps={{
                        hasClearButton: false,
                        className: CLASSNAME,
                        scope: CLASSNAME,
                        renderOption: (instance) => (
                            <SelectTextField.Option
                                before={
                                    <Thumbnail
                                        alt=""
                                        image={getInstanceFavIcon(instance)}
                                        size="s"
                                        fallback={<Thumbnail alt="" image={defaultFavIcon} size="s" />}
                                    />
                                }
                            >
                                <Text as="span" truncate>
                                    {instance.name}
                                </Text>
                            </SelectTextField.Option>
                        ),
                    }}
                    searchParams={{
                        ids: !isGlobalAdmin && isInstanceAdmin ? instancesWhereUserIsAdmin : undefined,
                    }}
                    searchOnlyAdministrableSites
                />
            ) : (
                <Text as="span" typography="subtitle2" className={element('instance-name')}>
                    {currentInstance?.name}
                </Text>
            )}
        </FlexBox>
    );
};
